import angular from 'angular';
import dateTemplate from './date.html';
import dateInlineTemplate from './date-inline.html';

const DAY = 'day';
const MONTH = 'month';
const DAY_FORMAT = 'M/d/yy';
const MONTH_FORMAT = 'M/yyyy';
const TIME_FORMAT_12 = 'hh:mm a';
const TIME_FORMAT_24 = 'HH:mm';

const INLINE_CONTAINER_SELECTOR = 'div.date-dropdown';
const INLINE_CLASS = 'akam-date-inline';

class DateController {
  static get $inject() {
    return [
      '$log', '$scope', '$translate', '$filter', 'moment', '$attrs', '$document',
      '$element', 'timezonesService', '$animate'
    ];
  }

  constructor($log, $scope, $translate, $filter, moment, $attrs, $document, $element,
    timezonesService, $animate) {
    this.$log = $log;
    this.$scope = $scope;
    this.$translate = $translate;
    this.$filter = $filter;
    this.moment = moment;
    this.$attrs = $attrs;
    this.$document = $document;
    this.$element = $element;
    this.timezonesService = timezonesService;
    this.$animate = $animate;
  }

  get moveForwardEventName() {
    return [this.eventNamespace, 'move-forward'].join(':');
  }

  get moveBackwardEventName() {
    return [this.eventNamespace, 'move-backward'].join(':');
  }

  $onInit() {
    this.$animate.enabled(this.$element, false);
    this.mode = this.mode || DAY;
    this.boundCloser = this.closeDropdown.bind(this);
    this.dateDropdown = angular.element(this.$element[0].querySelector('.date-dropdown'));
    this.hasTimezone = angular.isDefined(this.$attrs.timezone);
    this.clearable = angular.isDefined(this.$attrs.clearable);
    this.hasTime = angular.isDefined(this.$attrs.time) || this.hasTimezone;

    this.initTimeOptions();
    this.initPlaceholder();

    this.ngModel.$formatters.push(this.formatModel.bind(this));
    this.ngModel.$render = this.renderView.bind(this);
    this.ngModel.$parsers.push(this.parseView.bind(this));

    this.$scope.$on(this.moveForwardEventName, (event, toDate) => this.moveForward(toDate));
    this.$scope.$on(this.moveBackwardEventName, (event, toDate) => this.moveBackward(toDate));
  }

  moveForward(date) {
    this.dateModel = this.findNextAvailableDate(date);
    this.setDate(this.dateModel);
  }

  findNextAvailableDate(date) {
    if (this.isLowerThanMax(date) === false) {
      return null;
    }

    if (this.isDateDisabled(date)) {
      const nextDate = this.moment(date).add(1, 'day').toDate();
      return this.findNextAvailableDate(nextDate);
    }

    return date;
  }

  moveBackward(date) {
    this.dateModel = this.findPreviousAvailableDate(date);
    this.setDate(this.dateModel);
  }

  findPreviousAvailableDate(date) {
    if (this.isGreaterThanMin(date) === false) {
      return null;
    }

    if (this.isDateDisabled(date)) {
      const prevDate = this.moment(date).subtract(1, 'day').toDate();
      return this.findPreviousAvailableDate(prevDate);
    }

    return date;
  }

  isGreaterThanMin(date) {
    return this.min ? this.min <= date : true;
  }

  isLowerThanMax(date) {
    return this.max ? this.max >= date : true;
  }

  isDateDisabled(date) {
    return this.dateDisabled ? this.dateDisabled({date, mode: this.mode}) : false;
  }

  $onChanges(changes) {
    const minDate = this.filterDate(changes.min);
    const maxDate = this.filterDate(changes.max);

    if (minDate) {
      this.localeMin = minDate;
    }

    if (maxDate) {
      this.localeMax = maxDate;
    }

    this.setOptions();
  }

  setOptions() {
    this.options = {
      datepickerMode: this.mode,
      formatMonth: 'MMM',
      formatDay: 'd',
      minMode: this.mode,
      maxMode: this.mode,
      minDate: this.localeMin,
      maxDate: this.localeMax,
      showWeeks: false,
      dateDisabled: this.dateDisabled
    };
  }

  filterDate(m) {
    let dt;
    if (m && m.currentValue) {
      dt = new Date(m.currentValue);
      if (isNaN(dt.getTime()) || !this.dateModel) {
        return undefined;
      }
    }
    return dt;
  }

  initDateTimezone() {
    const timezoneId = this.ngModel.$options.getOption('timezone');
    if (timezoneId) {
      this.timezone = this.timezonesService.getTimezone(timezoneId);
    } else {
      this.timezone =
        this.timezonesService.getGuessedTimezone() || this.timezonesService.getGmtTimezone();
    }
  }

  formatModel(modelValue) {
    if (modelValue && modelValue.constructor !== Date) {
      throw new Error('akamDate: ngModel must be a Date object');
    }

    if (!this.timezone) {
      this.initDateTimezone();
    }

    if (!modelValue) {
      return undefined;
    }

    if (this.min) {
      this.localeMin = this.timezonesService.toTimezone(this.min, this.timezone.id);
    }

    if (this.max) {
      this.localeMax = this.timezonesService.toTimezone(this.max, this.timezone.id);
    }
    this.setOptions();
    return this.timezonesService.toTimezone(modelValue, this.timezone.id);
  }

  renderView() {
    if (!this.ngModel.$viewValue) {
      this.dateModel = undefined;
      this.timeModel = undefined;
      return undefined;
    }

    this.dateModel = this.ngModel.$viewValue;

    this.timeModel = {
      hours: this.dateModel.getHours(),
      minutes: this.dateModel.getMinutes()
    };

    this.setRangeTimes();
  }

  parseView(viewValue) {
    if (this.min) {
      this.localeMin = this.timezonesService.fromTimezone(this.min, this.timezone.id);
      this.setOptions();
    }
    if (this.max) {
      this.localeMax = this.timezonesService.fromTimezone(this.max, this.timezone.id);
      this.setOptions();
    }
    return this.timezonesService.fromTimezone(viewValue, this.timezone.id);
  }

  openTimezone($event) {
    $event.stopPropagation();
    this.timezoneOpened = true;
  }

  changeDate() {
    if (!this.timeModel) {
      this.timeModel = {
        hours: 0,
        minutes: 0
      };
    }
    this.dateModel.setHours(this.timeModel.hours);
    this.dateModel.setMinutes(this.timeModel.minutes);
    this.setDate(new Date(this.dateModel.getTime()));
  }

  initPlaceholder() {
    if (this.mode === DAY) {
      this.$translate(this.placeholder || 'components.date-picker.placeholder.date')
        .then(value => this.placeholder = value)
        .catch(value => this.placeholder = value);
    } else if (this.mode === MONTH) {
      this.$translate(this.placeholder || 'components.date-picker.placeholder.month')
        .then(value => this.placeholder = value)
        .catch(value => this.placeholder = value);
    }
  }

  initTimeOptions() {
    const dateFormat = this.mode === DAY ? DAY_FORMAT : MONTH_FORMAT;

    if (this.hasTime) {
      this.noMeridian = angular.isDefined(this.$attrs.noMeridian);
      const timeFormat = this.noMeridian ? TIME_FORMAT_24 : TIME_FORMAT_12;
      this.format = `${dateFormat} ${timeFormat}`;
    } else {
      this.format = dateFormat;
    }
  }

  toggleDropdown() {
    if (!this.on) {
      return;
    }
    if (this.dateDropdown.hasClass('ng-hide')) {
      this.hideAllDateDropdowns();
      this.dateDropdown.removeClass('ng-hide');
      this.$document.on('click', this.boundCloser);
    } else {
      this.dateDropdown.addClass('ng-hide');
      this.$document.off('click', this.boundCloser);
    }
  }

  hideAllDateDropdowns() {
    const dropdowns = this.$document[0].querySelectorAll('.date-dropdown');
    for (let i = 0; i < dropdowns.length; i++) {
      angular.element(dropdowns[i]).addClass('ng-hide');
    }
  }

  hideAllTimeDropdowns() {
    const dropdowns = this.$document[0].querySelectorAll('.akam-time.dropdown.open');
    for (let i = 0; i < dropdowns.length; i++) {
      angular.element(dropdowns[i]).removeClass('open');
    }
  }

  inTemplate(target) {
    let node = target;
    while (node[0].nodeName !== 'HTML') {
      if (node[0].nodeName === 'AKAM-DATE') {
        return true;
      } else {
        node = node.parent();
      }
    }
    return false;
  }

  closeDropdown($event) {
    const target = angular.element($event.target);

    if (this.timezoneOpened && !this.inTemplate(target)) {
      this.timezoneOpened = false;
      return;
    }

    if (target.controller('akamDate') && target[0].nodeName !== 'AKAM-DATE') {
      return;
    }

    this.dateDropdown.addClass('ng-hide');
    this.$document.off('click', this.boundCloser);
  }

  setTimezone() {
    if (!this.dateModel) {
      return undefined;
    }
    this.setDate(new Date(this.dateModel.getTime()));
  }

  clearShown() {
    return this.clearable && this.dateModel && this.on;
  }

  clearDate($event) {
    if ($event) {
      // clearing the date should keep the date dropdown open, but hide other dropdowns
      this.hideAllTimeDropdowns();
      $event.stopPropagation();
    }
    this.dateModel = undefined;
    this.timeModel = undefined;
    this.setDate(this.dateModel);
  }

  get formattedDate() {
    if (!this.dateModel) {
      return undefined;
    }
    let formattedDate = this.$filter('date')(this.dateModel, this.format);
    if (this.hasTimezone) {
      formattedDate += ` ${this.timezone.abbr}`;
    }
    return formattedDate;
  }

  get on() {
    return !this.isDisabled && !this.isReadonly;
  }

  setRangeTimes() {
    if (this.min && this.moment(this.dateModel).isSame(this.min, 'day')) {
      this.minTime = {
        hours: this.min.getHours(),
        minutes: this.min.getMinutes()
      };
    } else {
      this.minTime = undefined;
    }

    if (this.max && this.moment(this.dateModel).isSame(this.max, 'day')) {
      this.maxTime = {
        hours: this.max.getHours(),
        minutes: this.max.getMinutes()
      };
    } else {
      this.maxTime = undefined;
    }
  }

  updateTime() {
    this.dateModel.setHours(this.timeModel.hours);
    this.dateModel.setMinutes(this.timeModel.minutes);
    this.ngModel.$setViewValue(new Date(this.dateModel.getTime()));
    this.ngModel.$setTouched();
  }

  setDate(dateModel) {
    this.ngModel.$setViewValue(dateModel);
    this.ngModel.$setTouched();

    this.setRangeTimes();
  }
}

const dateComponent = {
  require: {
    ngModel: 'ngModel'
  },
  bindings: {
    placeholder: '@',
    mode: '@',
    format: '@',
    eventNamespace: '@',
    min: '<',
    max: '<',
    hourStep: '<',
    minuteStep: '<',
    isDisabled: '<',
    isReadonly: '<',
    dateDisabled: '<'
  },
  controller: DateController,
  template($element, $attrs) {
    let elem;

    if (angular.isDefined($attrs.inline)) {
      const dateInlineElem = angular.element(dateInlineTemplate);
      dateInlineElem.addClass(INLINE_CLASS);
      elem = dateInlineElem;
    } else {
      const dateElem = angular.element(dateTemplate);
      const inlineWrapper = angular.element(dateElem[0].querySelector(INLINE_CONTAINER_SELECTOR));

      inlineWrapper.empty().append(angular.element(dateInlineTemplate));
      elem = dateElem;
    }

    if (angular.isDefined($attrs.noMeridian)) {
      elem.find('akam-time').attr('no-meridian', '');
    }
    return elem[0].outerHTML;
  }
};

dateComponent.template.$inject = ['$element', '$attrs'];

export default dateComponent;
